import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { cn } from '@/shared/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

interface SearchInputProps {
  /** URL에 반영된 검색어 */
  value: string | undefined;
  placeholder: string;
  /** 입력이 멈추면(300ms) 호출한다 */
  onSearch: (keyword: string | undefined) => void;
  className?: string;
}

/** Figma Input(search) — 돋보기 + 입력. 타이핑 중에는 URL을 바꾸지 않는다 */
export function SearchInput({
  value,
  placeholder,
  onSearch,
  className,
}: SearchInputProps) {
  const [draft, setDraft] = useState(value ?? '');
  const debounced = useDebouncedValue(draft);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  // 뒤로 가기 등으로 URL 검색어가 바뀌면 입력값도 맞춘다
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    const next = debounced.trim() || undefined;
    if (next !== (value || undefined)) onSearchRef.current(next);
    // value는 비교용 — 바뀔 때마다 다시 검색하지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <label
      className={cn(
        // 데스크탑 Figma 280px, 모바일은 가로 전체
        'flex h-12 w-full shrink-0 items-center gap-3 rounded-16 border-[1.5px] border-line-grey bg-surface-white px-3 sm:w-[280px]',
        'focus-within:border-fg-accent',
        className,
      )}
    >
      <Icon name="search" size={24} />
      <input
        type="search"
        value={draft}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        className="min-w-0 flex-1 bg-transparent text-body-14 font-regular text-fg-primary outline-none placeholder:text-fg-light"
      />
    </label>
  );
}
